// Shared runtime for the YOLO Kidz browser viewer and the node verifier:
// voxel part decoding, per-node layer compositing, face meshing and animation sampling.

export const FPS = 24;
export const RLE_EMPTY = 255;
export const EMISSIVE_BIT = 1 << 24;

// Euler order VoxEdit uses for the x/y/z rotation tracks (verified against the glTF export by tools/verify.js).
export const EULER_ORDER = 'ZYX';
const RX_STRIDE = 4; // rotation-x keys carry the interpolation mode flag as a 4th element

// ---------- parts ----------
// Decode one RLE part from parts.bin into a Uint8Array of palette indices (x-major: i = x*sy*sz + y*sz + z).
export function decodePart(meta, bin) {
  const [sx, sy, sz] = meta.size;
  const grid = new Uint8Array(sx * sy * sz);
  let i = 0;
  for (let o = meta.off; o < meta.off + meta.len; o += 2) {
    const len = bin[o], v = bin[o + 1];
    grid.fill(v, i, i + len);
    i += len;
  }
  if (i !== grid.length) throw new Error(`${meta.name}: RLE decoded ${i} of ${grid.length} voxels`);
  return grid;
}

// Composite layers in order (later layers overwrite) into a packed colour grid:
// Int32Array with (r<<16 | g<<8 | b) plus EMISSIVE_BIT, or -1 for empty.
export function compositeLayers(layers) {
  if (!layers.length) return null;
  const { size, pivot } = layers[0].meta;
  const n = size[0] * size[1] * size[2];
  const grid = new Int32Array(n).fill(-1);
  for (const { meta, voxels } of layers) {
    if (meta.size.join() !== size.join()) throw new Error(`${meta.name}: size ${meta.size} differs from ${size}`);
    const packed = meta.palette.map(([r, g, b, e]) => (r << 16) | (g << 8) | b | (e ? EMISSIVE_BIT : 0));
    for (let i = 0; i < n; i++) {
      const v = voxels[i];
      if (v !== RLE_EMPTY) grid[i] = packed[v];
    }
  }
  return { size, pivot, grid };
}

// ---------- meshing ----------
const FACES = [
  { d: [ 1, 0, 0], c: [[1,0,0],[1,1,0],[1,1,1],[1,0,1]] },
  { d: [-1, 0, 0], c: [[0,0,1],[0,1,1],[0,1,0],[0,0,0]] },
  { d: [ 0, 1, 0], c: [[0,1,0],[0,1,1],[1,1,1],[1,1,0]] },
  { d: [ 0,-1, 0], c: [[0,0,0],[1,0,0],[1,0,1],[0,0,1]] },
  { d: [ 0, 0, 1], c: [[1,0,1],[1,1,1],[0,1,1],[0,0,1]] },
  { d: [ 0, 0,-1], c: [[0,0,0],[0,1,0],[1,1,0],[1,0,0]] },
];
const TRI = [0, 1, 2, 0, 2, 3];

// Exposed-face triangles for a composited node, split into lit and emissive buffers.
// Positions are voxel coordinates minus the part pivot, so the node origin is the joint.
// Colours are sRGB in 0..1 (the caller converts to linear if its renderer needs it).
export function buildFaces(comp) {
  const [sx, sy, sz] = comp.size;
  const grid = comp.grid;
  const px = comp.pivot[0] * sx, py = comp.pivot[1] * sy, pz = comp.pivot[2] * sz;
  const lit = { pos: [], nrm: [], col: [] };
  const glow = { pos: [], nrm: [], col: [] };
  const at = (x, y, z) => (x < 0 || y < 0 || z < 0 || x >= sx || y >= sy || z >= sz) ? -1 : grid[x * sy * sz + y * sz + z];
  for (let x = 0; x < sx; x++) for (let y = 0; y < sy; y++) for (let z = 0; z < sz; z++) {
    const v = grid[x * sy * sz + y * sz + z];
    if (v < 0) continue;
    const r = ((v >> 16) & 255) / 255, g = ((v >> 8) & 255) / 255, b = (v & 255) / 255;
    const buf = v & EMISSIVE_BIT ? glow : lit;
    for (const f of FACES) {
      if (at(x + f.d[0], y + f.d[1], z + f.d[2]) >= 0) continue;
      for (const k of TRI) {
        const c = f.c[k];
        buf.pos.push(x + c[0] - px, y + c[1] - py, z + c[2] - pz);
        buf.nrm.push(f.d[0], f.d[1], f.d[2]);
        buf.col.push(r, g, b);
      }
    }
  }
  return { lit, glow };
}

// ---------- animation ----------
export const EASE = [
  () => 0,                                                        // 0 instant (hold)
  t => t,                                                         // 1 linear
  t => t * t,                                                     // 2 quad in
  t => 1 - (1 - t) * (1 - t),                                     // 3 quad out
  t => t < 0.5 ? 2 * t * t : 1 - 2 * (1 - t) * (1 - t),           // 4 quad in/out
  t => t * t * t,                                                 // 5 cubic in
  t => 1 - (1 - t) ** 3,                                          // 6 cubic out
  t => t < 0.5 ? 4 * t ** 3 : 1 - 4 * (1 - t) ** 3,               // 7 cubic in/out
];

// Locate the key segment containing a frame in a flat track: [indexA, indexB, rawT].
// indexA === indexB when the frame is clamped to the first/last key.
function segment(track, stride, frame) {
  const n = track.length / stride;
  if (frame <= track[0]) return [0, 0, 0];
  const last = n - 1;
  if (frame >= track[last * stride]) return [last, last, 0];
  let i = 0;
  while (track[(i + 1) * stride] <= frame) i++;
  const fa = track[i * stride], fb = track[(i + 1) * stride];
  return [i, i + 1, (frame - fa) / (fb - fa)];
}

// Sample a flat track [frame, interp, value, ...] at a fractional frame with the key's easing.
export function sampleTrack(track, frame, dflt, stride = 3) {
  if (track.length === 0) return dflt;
  const [ia, ib, t] = segment(track, stride, frame);
  const va = track[ia * stride + 2];
  if (ia === ib) return va;
  const interp = track[ia * stride + 1], vb = track[ib * stride + 2];
  return va + (vb - va) * (EASE[interp] ?? EASE[1])(t);
}

function eulerAt(tr, frame, e) {
  e[0] = sampleTrack(tr[3], frame, 0, RX_STRIDE);
  e[1] = sampleTrack(tr[4], frame, 0);
  e[2] = sampleTrack(tr[5], frame, 0);
  return e;
}

const _e = [0, 0, 0];

// Rotation of a node at a frame as a quaternion. Segments whose x-key flag is set interpolate by
// slerp between the poses at the two keys (with the key's easing); otherwise each Euler axis is eased
// independently. Both give the exact key pose at key frames.
export function sampleRotation(tr, frame, out) {
  const rx = tr[3];
  if (rx.length === 0) return eulerToQuat(eulerAt(tr, frame, _e), EULER_ORDER, out);
  const [ia, ib, t] = segment(rx, RX_STRIDE, frame);
  if (ia === ib || !rx[ia * RX_STRIDE + 3]) return eulerToQuat(eulerAt(tr, frame, _e), EULER_ORDER, out);
  const cache = tr.keyQuats || (tr.keyQuats = []);
  const keyQuat = i => cache[i] || (cache[i] = eulerToQuat(eulerAt(tr, rx[i * RX_STRIDE], [0, 0, 0]), EULER_ORDER, [0, 0, 0, 1]));
  const interp = rx[ia * RX_STRIDE + 1];
  return slerp(keyQuat(ia), keyQuat(ib), (EASE[interp] ?? EASE[1])(t), out);
}

// Shortest-path spherical interpolation of quaternions a -> b into out.
export function slerp(a, b, t, out) {
  let bx = b[0], by = b[1], bz = b[2], bw = b[3];
  let dot = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
  if (dot < 0) { bx = -bx; by = -by; bz = -bz; bw = -bw; dot = -dot; }
  let wa, wb;
  if (dot > 0.9995) { wa = 1 - t; wb = t; }
  else {
    const theta = Math.acos(Math.min(1, dot)), s = Math.sin(theta);
    wa = Math.sin((1 - t) * theta) / s; wb = Math.sin(t * theta) / s;
  }
  out[0] = a[0] * wa + bx * wb; out[1] = a[1] * wa + by * wb;
  out[2] = a[2] * wa + bz * wb; out[3] = a[3] * wa + bw * wb;
  const n = Math.hypot(out[0], out[1], out[2], out[3]);
  out[0] /= n; out[1] /= n; out[2] /= n; out[3] /= n;
  return out;
}

// Pose of one rig node at a frame: out.p = [x,y,z], out.q = quaternion [x,y,z,w], out.s = uniform scale.
export function samplePose(anim, nodeIdx, frame, out) {
  const tr = anim.nodes[nodeIdx];
  out.p[0] = sampleTrack(tr[0], frame, 0);
  out.p[1] = sampleTrack(tr[1], frame, 0);
  out.p[2] = sampleTrack(tr[2], frame, 0);
  sampleRotation(tr, frame, out.q);
  out.s = sampleTrack(tr[6], frame, 1);
  return out;
}

export const newPose = () => ({ p: [0, 0, 0], q: [0, 0, 0, 1], s: 1 });

// Euler (radians) to quaternion [x,y,z,w]; order 'XYZ' means q = qx * qy * qz (same convention as three.js).
export function eulerToQuat(e, order, out = [0, 0, 0, 1]) {
  let x = 0, y = 0, z = 0, w = 1;
  for (const axis of order) {
    const a = e['XYZ'.indexOf(axis)] / 2;
    const s = Math.sin(a), c = Math.cos(a);
    const bx = axis === 'X' ? s : 0, by = axis === 'Y' ? s : 0, bz = axis === 'Z' ? s : 0, bw = c;
    const nx = w * bx + x * bw + y * bz - z * by;
    const ny = w * by - x * bz + y * bw + z * bx;
    const nz = w * bz + x * by - y * bx + z * bw;
    const nw = w * bw - x * bx - y * by - z * bz;
    x = nx; y = ny; z = nz; w = nw;
  }
  out[0] = x; out[1] = y; out[2] = z; out[3] = w;
  return out;
}
